import React, { useState, useEffect } from 'react';
import { Shield, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
} from '@roger/ui-components';
import type { PatientInsuranceResponse } from '../../services/api/types.gen';
import { formatDateToMMDDYYYY } from '../../utils/dateUtils';
import { patientInsuranceSectionPath } from '../../utils/navigation';
import { EligibilityCheckSummary } from '../eligibility/EligibilityCheckSummary';
import { usePatientEligibilityChecks } from '../../hooks/usePatientEligibilityChecks';

interface EpisodeInsuranceSelectorProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (insuranceId: string) => Promise<void>;
  patientInsurances: PatientInsuranceResponse[];
  currentInsuranceId: string | null | undefined;
  insurancesLoading?: boolean;
  patientId?: string;
}

export const EpisodeInsuranceSelector: React.FC<
  EpisodeInsuranceSelectorProps
> = ({
  open,
  onClose,
  onConfirm,
  patientInsurances,
  currentInsuranceId,
  insurancesLoading,
  patientId,
}) => {
  const navigate = useNavigate();
  // Gated on `open`: this dialog is rendered unconditionally from PatientDetail
  // and hides itself, so an ungated fetch would run on every chart open.
  const {
    latestFor,
    resolving: checksResolving,
    failed: checksFailed,
  } = usePatientEligibilityChecks(patientId, open);
  const [selectedInsuranceId, setSelectedInsuranceId] = useState<string | null>(
    null
  );
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset selection when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedInsuranceId(null);
      setShowConfirmation(false);
    }
  }, [open]);

  const handleInsuranceClick = (insuranceId: string) => {
    // Don't allow selecting the already-assigned insurance
    if (insuranceId === currentInsuranceId) {
      return;
    }
    setSelectedInsuranceId(insuranceId);
    setShowConfirmation(true);
  };

  const handleAddEditInsurancesClick = () => {
    if (!patientId) return;
    onClose();
    navigate(patientInsuranceSectionPath(patientId));
  };

  const handleConfirm = async () => {
    if (!selectedInsuranceId) return;

    setIsSubmitting(true);
    try {
      await onConfirm(selectedInsuranceId);
      onClose();
    } catch (error) {
      console.error('Failed to update episode insurance:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    setShowConfirmation(false);
    setSelectedInsuranceId(null);
  };

  const selectedInsurance = patientInsurances.find(
    ins => ins.id === selectedInsuranceId
  );

  if (showConfirmation && selectedInsurance) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className='w-[calc(100vw-2rem)] sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>Confirm Insurance Selection</DialogTitle>
            <DialogDescription>
              You are about to set this insurance as the primary for this
              episode.
            </DialogDescription>
          </DialogHeader>

          <div className='rounded-lg border border-border p-4 space-y-2'>
            <p className='font-medium text-sm'>
              {selectedInsurance.insurance_name}
            </p>
            <div className='text-xs text-muted-foreground space-y-1'>
              {selectedInsurance.plan_name && (
                <p>Plan: {selectedInsurance.plan_name}</p>
              )}
              {selectedInsurance.policy_number && (
                <p>Policy: {selectedInsurance.policy_number}</p>
              )}
              {selectedInsurance.effective_start && (
                <p>
                  Effective:{' '}
                  {formatDateToMMDDYYYY(selectedInsurance.effective_start)}
                  {selectedInsurance.effective_end &&
                    ` – ${formatDateToMMDDYYYY(selectedInsurance.effective_end)}`}
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              onClick={handleBack}
              disabled={isSubmitting}
            >
              Back
            </Button>
            <Button
              type='button'
              onClick={handleConfirm}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  const hasCurrentInsurance = patientInsurances.some(
    ins => ins.id === currentInsuranceId
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className='w-[calc(100vw-2rem)] sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>Select Episode Insurance</DialogTitle>
          <DialogDescription>
            {!hasCurrentInsurance && currentInsuranceId === null && (
              <span className='block mb-2 text-amber-600 dark:text-amber-500 font-medium'>
                No insurance currently selected for this episode.
              </span>
            )}
            Click an insurance to attach it as the primary for this episode.{' '}
            {patientId && (
              <button
                type='button'
                onClick={handleAddEditInsurancesClick}
                className='text-primary hover:underline'
              >
                Click here to add or edit the patient's insurances.
              </button>
            )}
          </DialogDescription>
        </DialogHeader>

        {insurancesLoading ? (
          <p className='text-sm text-muted-foreground py-6 text-center'>
            Loading insurances…
          </p>
        ) : patientInsurances.length === 0 ? (
          <p className='text-sm text-muted-foreground py-6 text-center'>
            No insurance policies on file.
          </p>
        ) : (
          <div className='divide-y max-h-[400px] overflow-y-auto'>
            {patientInsurances.map(ins => {
              const isCurrent = ins.id === currentInsuranceId;
              return (
                <button
                  key={ins.id}
                  type='button'
                  onClick={() => handleInsuranceClick(ins.id)}
                  disabled={isCurrent}
                  className='w-full py-3 px-3 flex items-center gap-3 hover:bg-muted transition-colors text-left rounded-md disabled:opacity-60 disabled:cursor-not-allowed'
                >
                  <Shield className='h-4 w-4 text-muted-foreground shrink-0' />
                  <div className='flex-1 min-w-0'>
                    <div className='flex items-center gap-2'>
                      <p className='text-sm font-medium truncate'>
                        {ins.insurance_name}
                      </p>
                      {isCurrent && (
                        <span className='inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-primary/10 text-primary text-[10px] font-medium'>
                          <Check className='h-3 w-3' />
                          Current
                        </span>
                      )}
                    </div>
                    <p className='text-xs text-muted-foreground'>
                      {[
                        ins.plan_name,
                        ins.policy_number
                          ? `Policy: ${ins.policy_number}`
                          : null,
                        ins.effective_start
                          ? `${formatDateToMMDDYYYY(ins.effective_start)}${ins.effective_end ? ` – ${formatDateToMMDDYYYY(ins.effective_end)}` : ''}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                    {/* Coverage is most likely to have gone stale exactly here,
                        at a new episode or recert. */}
                    <div className='mt-1'>
                      <EligibilityCheckSummary
                        check={latestFor(ins)}
                        loading={checksResolving}
                        failed={checksFailed}
                      />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button type='button' variant='outline' onClick={onClose}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
